import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BlankValueComponent, CustomCellComponent } from '@vendasta/galaxy/table';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CrmObjectService, CrmFieldService, StandardIds } from '../../shared-services';
import { PAGE_ROUTES } from '../../constants';
import { CrmInjectionToken, CrmDependencies } from '../../tokens-and-interfaces';

const NO_VALUE_FOR_COMPANY_NAME = '<NO_VALUE_FOR_COMPANY_NAME>';
interface PrimaryCompanyNameData {
  displayValue: string;
  link: string;
}

@Component({
  template: `
    <ng-container *ngIf="componentData$ | async as data; else loading">
      <a *ngIf="data.link as link; else div" [routerLink]="link">
        <ng-container
          [ngTemplateOutlet]="displayNameTemplate"
          [ngTemplateOutletContext]="{ displayName: data.displayValue }"
        ></ng-container>
      </a>
      <ng-template #div>
        <div
          [ngTemplateOutlet]="displayNameTemplate"
          [ngTemplateOutletContext]="{ displayName: data.displayValue }"
        ></div>
      </ng-template>
    </ng-container>
    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
    <ng-template #displayNameTemplate let-displayName="displayName">
      <ng-container *ngIf="displayName !== NO_VALUE_FOR_COMPANY_NAME; else blank">{{ displayName }}</ng-container>
    </ng-template>
    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  standalone: true,
  styleUrls: ['./shared.scss'],
  imports: [RouterLink, CommonModule, BlankValueComponent],
})
export class PrimaryCompanyNameComponent extends CustomCellComponent {
  NO_VALUE_FOR_COMPANY_NAME = NO_VALUE_FOR_COMPANY_NAME;
  private companyId$$ = new BehaviorSubject<string>('');
  set value(newValue: string) {
    this.companyId$$.next(newValue);
  }

  displayValue$ = this.companyId$$.pipe(
    switchMap((contactPrimaryCompanyID) => {
      if (!contactPrimaryCompanyID) {
        return of(null);
      }
      return this.crmObjectService.getMultiObject('Company', [contactPrimaryCompanyID]);
    }),
    map((getMultiResponse) => {
      if (getMultiResponse?.crmObjects?.length === 0 || !getMultiResponse?.crmObjects?.[0].crmObjectId) {
        return null;
      }
      return this.crmFieldService.getFieldValueFromCrmObject(getMultiResponse?.crmObjects[0], StandardIds.CompanyName);
    }),
    map((companyNameFieldValue) => companyNameFieldValue?.stringValue ?? this.NO_VALUE_FOR_COMPANY_NAME),
  );
  link$ = this.config.viewProfileFlag$.pipe(
    take(1),
    switchMap((featureFlag) => {
      if (featureFlag) {
        return this.config.routePrefix$;
      } else {
        return of('');
      }
    }),
    map((routePrefix) => {
      if (!routePrefix || !this.companyId$$.getValue()) {
        return '';
      }
      const primaryCompanyProfileViewRoute = `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
      return `${routePrefix}/${primaryCompanyProfileViewRoute}`.replace(':crmObjectId', this.companyId$$.getValue());
    }),
  );
  componentData$: Observable<PrimaryCompanyNameData> = combineLatest([this.displayValue$, this.link$]).pipe(
    map(([displayValue, link]) => ({ displayValue, link })),
  );

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmFieldService: CrmFieldService,
    private readonly crmObjectService: CrmObjectService,
  ) {
    super();
  }
}
